import * as Yup from "yup";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import React, { FC, useState } from "react";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { useDispatch } from "react-redux";

import BaseInput from "../../elements/BaseInput";
import Button from "../../elements/Button";
import FormFieldError from "../../elements/FormFieldError";
import PhoneInputField from "../../elements/PhoneInputField";
import api from "../../../api";
import classes from "./CreateAdvertiserModal.module.scss";
import { AdvertiserType } from "../../../types/advertiserTypes";
import { generateBrandgenAdvertiserUuid } from "../../../utils/generateBrandgenUuid";
import { infoToaster } from "../../../helpers/toasters";
import { setAgencyAdvertisers } from "../../../redux/reducers/agency";
import { useCurrentAuthenticatedUser } from "../../../hooks/useCurrentAuthenticatedUser";

type CreateAdvertiserModalProps = {
  isOpen: boolean;
  handleClose: () => void;
};

type FormInitialValues = {
  name: string;
  phoneNumber: string;
  firstAddress: string;
  secondAddress: string;
  city: string;
  state: string;
  postalCode: string;
  domain: string;
};

const createAdvertiser = async (data: AdvertiserType) => {
  try {
    await api.createAdvertiser(data);
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const CreateAdvertiserModal: FC<CreateAdvertiserModalProps> = ({ isOpen, handleClose }) => {
  const formInitialValues: FormInitialValues = {
    name: "",
    phoneNumber: "",
    firstAddress: "",
    secondAddress: "",
    city: "",
    state: "",
    postalCode: "",
    domain: "",
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Required field."),
    phoneNumber: Yup.string().required("Required field."),
    firstAddress: Yup.string().required("Required field."),
    city: Yup.string().required("Required field."),
    state: Yup.string().required("Required field."),
    postalCode: Yup.string().required("Required field."),
    domain: Yup.string()
      .required("Required field.")
      .matches(
        /^(?:ht{2}ps?:\/\/)?(?:[\dA-Za-z-]+\.)+[A-Za-z]{2,}(?::\d+)?(?:#\S*)?$/,
        "Must be a valid domain without path",
      ),
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { currentUsername, agencyId } = useCurrentAuthenticatedUser();

  const dispatch = useDispatch();

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    try {
      setIsLoading(true);
      const { name, phoneNumber, firstAddress, secondAddress, city, state, postalCode, domain } = values;
      const newBrandgenAdvertiserId = generateBrandgenAdvertiserUuid();
      const advertiserToCreate: Partial<AdvertiserType> = {
        name,
        phone: phoneNumber,
        address_1: firstAddress,
        address_2: secondAddress,
        city,
        state,
        zip: postalCode,
        domain,
        brandgenAdvertiserId: newBrandgenAdvertiserId,
        createdBy: currentUsername,
        parentAgencyId: agencyId,
        category: "IAB3",
      };
      await createAdvertiser(advertiserToCreate as AdvertiserType);
      dispatch(setAgencyAdvertisers(agencyId));
      infoToaster("The selected advertising must have a connected payment method to configure a campaign!", 2000);
      setIsLoading(false);
      handleClose();
    } catch (error) {
      setIsLoading(false);
      actions.resetForm();
      const err = error as any;
      setErrorMessage(`There was an error: ${err.message}`);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isLoading ? () => null : handleClose}
      aria-labelledby="Advertiser Creation Modal"
      className={classes.container}
    >
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={false}
      >
        {({ handleBlur, setFieldValue, values, touched }) => (
          <>
            <div className={classes.dialogTitle}>
              <Typography variant="h2">Create New Advertiser</Typography>
            </div>
            <Form>
              <DialogContent className={classes.dialogContent}>
                <div className={classes.twoColumnInputs}>
                  <div className={classes.input}>
                    <Field
                      as={BaseInput}
                      value={values.name}
                      error={!!errorMessage}
                      name="name"
                      variant="outlined"
                      label="Name *"
                    />

                    <FormFieldError name="name" />
                  </div>

                  <div className={classes.input}>
                    <PhoneInputField
                      field={{
                        name: "phoneNumber",
                        value: values.phoneNumber,
                      }}
                      form={{
                        setFieldValue,
                        handleBlur,
                        touched,
                      }}
                      label="Phone Number *"
                      country="US"
                    />
                    <FormFieldError name="phoneNumber" />
                  </div>
                </div>

                <div className={classes.input}>
                  <Field
                    as={BaseInput}
                    value={values.firstAddress}
                    error={!!errorMessage}
                    name="firstAddress"
                    variant="outlined"
                    label="Address *"
                  />
                  <FormFieldError name="firstAddress" />
                </div>

                <div className={classes.input}>
                  <Field
                    as={BaseInput}
                    value={values.secondAddress}
                    error={!!errorMessage}
                    name="secondAddress"
                    variant="outlined"
                    label="Address 2"
                  />

                  <FormFieldError name="secondAddress" />
                </div>

                <div className={classes.twoColumnInputs}>
                  <div className={classes.input}>
                    <Field
                      as={BaseInput}
                      value={values.city}
                      error={!!errorMessage}
                      name="city"
                      variant="outlined"
                      label="City *"
                    />

                    <FormFieldError name="city" />
                  </div>

                  <div className={classes.input}>
                    <Field
                      as={BaseInput}
                      value={values.state}
                      error={!!errorMessage}
                      name="state"
                      variant="outlined"
                      label="State *"
                    />

                    <FormFieldError name="state" />
                  </div>
                </div>

                <div className={classes.twoColumnInputs}>
                  <div className={classes.input}>
                    <Field
                      as={BaseInput}
                      value={values.postalCode}
                      error={!!errorMessage}
                      name="postalCode"
                      variant="outlined"
                      label="Postal Code *"
                    />

                    <FormFieldError name="postalCode" />
                  </div>

                  <div className={classes.input}>
                    <Field
                      as={BaseInput}
                      value={values.domain}
                      error={!!errorMessage}
                      name="domain"
                      variant="outlined"
                      label="Website *"
                    />

                    <FormFieldError name="domain" />
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={isLoading ? () => null : handleClose} variant="outlined">
                  Cancel
                </Button>
                <Button type="submit" isLoading={isLoading} loaderSize={25}>
                  Submit
                </Button>
              </DialogActions>
            </Form>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateAdvertiserModal;
